import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * useVisitLive — sincronizzazione real-time multi-postazione.
 * - Ping presence + poll /visits/{vid}/live ogni 5s (since = updated_at corrente)
 * - Interval stabile per vid (stato letto via ref: niente raffiche di tick a ogni render)
 * - Anti self-echo: changed=true entro 8s dall'ultimo salvataggio locale riuscito viene
 *   ignorato (è quasi certamente l'eco del proprio autosave); una modifica reale di
 *   un'altra postazione viene comunque colta dal tick successivo.
 * - Ritorna { activeSessions, lastSync, conflict, dismissConflict }
 */
export default function useVisitLive({ vid, currentUpdatedAt, isDirty, onRemoteUpdate, workstation = "Postazione", getLastLocalSaveAt }) {
  const [activeSessions, setActiveSessions] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [conflict, setConflict] = useState(null); // { at, by }
  const sessionIdRef = useRef(null);
  const intervalRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { currentUpdatedAt, isDirty, onRemoteUpdate, workstation, getLastLocalSaveAt };

  // Stable session id per tab
  useEffect(() => {
    if (!sessionIdRef.current) {
      const exist = sessionStorage.getItem(`pv-session-${vid}`);
      if (exist) sessionIdRef.current = exist;
      else {
        sessionIdRef.current = `s_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
        sessionStorage.setItem(`pv-session-${vid}`, sessionIdRef.current);
      }
    }
  }, [vid]);

  const tick = useCallback(async () => {
    if (!vid || !sessionIdRef.current) return;
    const s = stateRef.current;
    try {
      // ping presence
      await api.post(`/visits/${vid}/presence`, { session_id: sessionIdRef.current, workstation: s.workstation });
      // live status
      const params = { exclude_session: sessionIdRef.current };
      if (s.currentUpdatedAt) params.since = s.currentUpdatedAt;
      const { data } = await api.get(`/visits/${vid}/live`, { params });
      setActiveSessions(data.active_sessions || []);
      setLastSync(new Date().toISOString());
      if (data.changed && data.visit) {
        const lastLocalSave = s.getLastLocalSaveAt ? s.getLastLocalSaveAt() : 0;
        if (lastLocalSave && Date.now() - lastLocalSave < 8000) return; // self-echo del proprio autosave
        if (stateRef.current.isDirty) {
          // conflitto: l'utente sta editando e ne arriva una versione remota più recente
          const otherSession = (data.active_sessions || [])[0];
          setConflict({ at: data.updated_at, by: otherSession?.user_name || otherSession?.workstation || "altra postazione" });
        } else {
          stateRef.current.onRemoteUpdate && stateRef.current.onRemoteUpdate(data.visit);
        }
      }
    } catch { /* silent */ }
  }, [vid]);

  useEffect(() => {
    if (!vid) return;
    tick(); // immediate (una sola volta per vid)
    intervalRef.current = setInterval(tick, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [tick, vid]);

  return { activeSessions, lastSync, conflict, dismissConflict: () => setConflict(null), sessionId: sessionIdRef.current };
}
